import React from 'react';
import type { Artboard, CanvasObject } from '../../../types';

type Props = {
	zOrder: string[];
	objects: Map<string, CanvasObject>;
	selectedId: string | null;
	selectedIds: Set<string> | null;
	draggingLayerId: string | null;
	dragOverLayerId: string | null;
	dropAbove: boolean;
	setDraggingLayerId: (id: string | null) => void;
	setDragOverLayerId: (id: string | null) => void;
	setDropAbove: (v: boolean) => void;
	removeFromSelection: (id: string) => void;
	addToSelection: (id: string) => void;
	setSelection: (ids: string[]) => void;
	moveObjectsInZOrder: (draggedIds: string[], targetId: string, dropAbove: boolean) => void;
	updateObject: (id: string, updater: (obj: CanvasObject) => CanvasObject) => void;
	saveToHistory: (state: { objects: Map<string, CanvasObject>; zOrder: string[]; selectedId: string | null }) => void;
	requestRender: () => void;
	artboards: Artboard[];
	filterResetKey: number;
};

export const LayerList: React.FC<Props> = ({
	zOrder,
	objects,
	selectedId,
	selectedIds,
	draggingLayerId,
	dragOverLayerId,
	dropAbove,
	setDraggingLayerId,
	setDragOverLayerId,
	setDropAbove,
	removeFromSelection,
	addToSelection,
	setSelection,
	moveObjectsInZOrder,
	updateObject,
	saveToHistory,
	requestRender,
	artboards,
	filterResetKey
}) => {
	const [editingLayerId, setEditingLayerId] = React.useState<string | null>(null);
	const [editingName, setEditingName] = React.useState<string>('');
	const [filterArtboardId, setFilterArtboardId] = React.useState<string>('all');
	const inputRefs = React.useRef<Map<string, HTMLInputElement>>(new Map());

	const filteredLayerIds = React.useMemo(() => {
		if (filterArtboardId === 'all') return zOrder;
		return zOrder.filter((id) => objects.get(id)?.artboardId === filterArtboardId);
	}, [filterArtboardId, zOrder, objects]);

	React.useEffect(() => {
		setFilterArtboardId('all');
	}, [filterResetKey]);

	// 편집 모드로 전환
	const startEditing = React.useCallback((id: string, currentName: string) => {
		setEditingLayerId(id);
		setEditingName(currentName);
	}, []);

	// 편집 완료 (저장)
	const finishEditing = React.useCallback((id: string, save: boolean) => {
		if (save && editingLayerId === id) {
			const trimmedName = editingName.trim();
			updateObject(id, (obj) => ({
				...obj,
				name: trimmedName || undefined
			}));
			saveToHistory({ objects, zOrder, selectedId });
		}
		setEditingLayerId(null);
		setEditingName('');
	}, [editingLayerId, editingName, updateObject, saveToHistory, objects, zOrder, selectedId]);

	// 입력 필드 포커스
	React.useEffect(() => {
		if (editingLayerId) {
			const input = inputRefs.current.get(editingLayerId);
			if (input) {
				input.focus();
				input.select();
			}
		}
	}, [editingLayerId]);
	return (
		<div className="layer-panel" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
			<div className="layer-filter">
				<select
					className="layer-filter__select"
					value={filterArtboardId}
					onChange={(e) => setFilterArtboardId(e.target.value)}
					title="아트보드별 레이어 필터"
				>
					<option value="all">전체 아트보드</option>
					{artboards.map((artboard) => (
						<option key={artboard.id} value={artboard.id}>
							{artboard.name}
						</option>
					))}
				</select>
			</div>
			<div className="layer-list" style={{ flex: 1, minHeight: 0 }}>
				{filteredLayerIds.length > 0 ? (
					[...filteredLayerIds].reverse().map((id) => {
						const obj = objects.get(id);
						if (!obj) return null;
						const isSelected = selectedIds?.has(id) || selectedId === id;
						const defaultLabel = obj.type === 'rectangle' ? '사각형' :
							obj.type === 'circle' ? '원' :
							obj.type === 'image' ? (obj.fileName || '이미지') :
							obj.type;
						const displayName = obj.name || defaultLabel;
						const isDragging = draggingLayerId === id;
						const isDragOver = dragOverLayerId === id;
						const isEditing = editingLayerId === id;
						const isLocked = !!obj.isLocked;
						return (
							<div
								key={id}
								className={`layer-item ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
								data-drop-above={isDragOver ? dropAbove : undefined}
								draggable
								onDragStart={(e) => {
									const idsToDrag = selectedIds && selectedIds.size > 1 ? Array.from(selectedIds) : [id];
									setDraggingLayerId(id);
									e.dataTransfer.effectAllowed = 'move';
									e.dataTransfer.setData('text/plain', JSON.stringify(idsToDrag));
								}}
								onDragOver={(e) => {
									e.preventDefault();
									e.dataTransfer.dropEffect = 'move';
									const draggedIds = JSON.parse(e.dataTransfer.getData('text/plain') || '[]') as string[];
									if (draggedIds.includes(id)) return;
									setDragOverLayerId(id);
									const targetElement = e.currentTarget as HTMLElement;
									const targetRect = targetElement.getBoundingClientRect();
									const centerY = targetRect.top + targetRect.height / 2;
									const isAbove = e.clientY < centerY;
									setDropAbove(isAbove);
								}}
								onDragLeave={() => {
									setDragOverLayerId(null);
									setDropAbove(false);
								}}
								onDrop={(e) => {
									e.preventDefault();
									const draggedIds = JSON.parse(e.dataTransfer.getData('text/plain') || '[]') as string[];
									if (draggedIds.includes(id)) {
										setDraggingLayerId(null);
										setDragOverLayerId(null);
										setDropAbove(false);
										return;
									}
									moveObjectsInZOrder(draggedIds, id, dropAbove);
									saveToHistory({ objects, zOrder, selectedId });
									setDraggingLayerId(null);
									setDragOverLayerId(null);
									setDropAbove(false);
									requestRender();
								}}
								onDragEnd={() => {
									setDraggingLayerId(null);
									setDragOverLayerId(null);
									setDropAbove(false);
								}}
								onClick={(e) => {
									if (draggingLayerId || isEditing) return;
									if (e.shiftKey || e.ctrlKey || e.metaKey) {
										if (selectedIds?.has(id)) {
											removeFromSelection(id);
										} else {
											addToSelection(id);
										}
									} else {
										setSelection([id]);
									}
								}}
								onDoubleClick={(e) => {
									if (draggingLayerId || isEditing) return;
									e.stopPropagation();
									startEditing(id, obj.name || '');
								}}
							>
								{obj.isClipped && obj.clipMaskId && (
									<div className="layer-item__clip-icon">
										<div className="layer-item__clip-mask-icon" title="클리핑 마스크 적용됨">
											<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
												<circle cx="8" cy="8" r="7" fill="#dc3545" stroke="#fff" strokeWidth="1"/>
												<path d="M8 10L5 7L11 7L8 10Z" fill="white"/>
											</svg>
										</div>
									</div>
								)}
								<div
									className="layer-item__preview"
									style={{
										backgroundColor: obj.type === 'text' ? '#e9ecef' : (obj.backgroundColor || '#007bff'),
										border: (() => {
											if (obj.type === 'text') return 'none';
											const bgColor = obj.backgroundColor || '#007bff';
											const isWhite = bgColor.toLowerCase() === '#fff' || bgColor.toLowerCase() === '#ffffff' || bgColor.toLowerCase() === 'white';
											if (isWhite) {
												return '1px solid #dee2e6';
											}
											return 'none';
										})(),
										borderRadius: obj.type === 'circle' ? '50%' : '0',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										fontSize: '12px',
										fontWeight: 'bold',
										color: '#495057'
									}}
								>
									{obj.type === 'text' ? (
										'T'
									) : obj.type === 'image' && obj.imageSrc ? (
										<img src={obj.imageSrc} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
									) : null}
								</div>
								{isEditing ? (
									<input
										ref={(el) => {
											if (el) {
												inputRefs.current.set(id, el);
											} else {
												inputRefs.current.delete(id);
											}
										}}
										className="layer-item__name-input"
										value={editingName}
										onChange={(e) => setEditingName(e.target.value)}
										onBlur={() => finishEditing(id, true)}
										onKeyDown={(e) => {
											if (e.key === 'Enter') {
												e.preventDefault();
												finishEditing(id, true);
											} else if (e.key === 'Escape') {
												e.preventDefault();
												finishEditing(id, false);
											}
										}}
										onClick={(e) => e.stopPropagation()}
										onDoubleClick={(e) => e.stopPropagation()}
									/>
								) : (
									<span className="layer-item__name">{displayName}</span>
								)}
								{obj.type === 'image' && (
									<button
										type="button"
										className={`layer-item__lock-btn ${isLocked ? 'is-locked' : ''}`}
										title={isLocked ? '레이어 잠금 해제' : '레이어 잠금'}
										aria-label={isLocked ? '레이어 잠금 해제' : '레이어 잠금'}
										onClick={(e) => {
											e.stopPropagation();
											updateObject(id, (current) => ({
												...current,
												isLocked: !current.isLocked
											}));
											saveToHistory({ objects, zOrder, selectedId });
											requestRender();
										}}
									>
										{isLocked ? (
											<svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d="M18 10H17V7C17 4.24 14.76 2 12 2C9.24 2 7 4.24 7 7V10H6C4.9 10 4 10.9 4 12V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V12C20 10.9 19.1 10 18 10ZM9 7C9 5.34 10.34 4 12 4C13.66 4 15 5.34 15 7V10H9V7Z" fill="currentColor"/>
											</svg>
										) : (
											<svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
												<path d="M18 10H9V7C9 5.34 10.34 4 12 4C13.31 4 14.42 4.83 14.83 6H16.92C16.44 3.16 13.97 1 12 1C8.69 1 6 3.69 6 7V10H6C4.9 10 4 10.9 4 12V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V12C20 10.9 19.1 10 18 10Z" fill="currentColor"/>
											</svg>
										)}
									</button>
								)}
							</div>
						);
					})
				) : (
					<div style={{ padding: '12px', color: '#6c757d', fontSize: '12px', textAlign: 'center' }}>
						레이어가 없습니다
					</div>
				)}
			</div>
		</div>
	);
};


